import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Plus } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";

const logTotal = (l) => (l.compensation || 0) + (l.approved_expenses || 0) + (l.detention || 0);

const CreateInvoiceForm = ({ approvedLogs, onCreated }) => {
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [periodLabel, setPeriodLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const toggleLog = (id) => setSelectedLogIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const createInvoice = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await brokerApi.post("/invoices", { period_label: periodLabel, service_log_ids: selectedLogIds });
      toast.success("Invoice generated.");
      setPeriodLabel("");
      setSelectedLogIds([]);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate invoice.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <form onSubmit={createInvoice} data-testid="broker-create-invoice-form" className="bg-white border border-rr-line rounded-2xl p-6 max-w-xl space-y-3">
      <input
        required
        data-testid="create-invoice-period-input"
        placeholder="Period label (e.g. Week of Aug 18)"
        value={periodLabel}
        onChange={(e) => setPeriodLabel(e.target.value)}
        className="w-full border border-rr-line rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-rr-orange"
      />
      <div className="text-xs font-semibold text-rr-muted">Select approved, unbilled service logs:</div>
      <div className="max-h-48 overflow-y-auto border border-rr-line rounded-lg divide-y divide-rr-line">
        {approvedLogs.length === 0 && <p data-testid="create-invoice-empty-logs" className="text-xs text-rr-muted p-3">No approved, unbilled service logs right now.</p>}
        {approvedLogs.map((l) => (
          <label key={l.id} className="flex items-center gap-2.5 px-3 py-2.5 text-sm cursor-pointer">
            <input type="checkbox" checked={selectedLogIds.includes(l.id)} onChange={() => toggleLog(l.id)} data-testid={`create-invoice-log-checkbox-${l.id}`} />
            {l.origin} → {l.destination} · ${logTotal(l).toFixed(2)}
          </label>
        ))}
      </div>
      <button type="submit" disabled={creating || selectedLogIds.length === 0} data-testid="create-invoice-submit-button" className="bg-rr-orange text-white font-bold px-6 py-3 rounded-full hover:bg-rr-navy transition-colors flex items-center gap-2 disabled:opacity-60">
        {creating ? <Loader2 size={16} className="animate-spin" /> : <><Plus size={15} /> Generate Invoice</>}
      </button>
    </form>
  );
};

const InvoiceRow = ({ invoice: inv, expanded, lineItems, onToggleDetails, onStatusChange }) => (
  <div data-testid={`broker-invoice-row-${inv.id}`} className="bg-white border border-rr-line rounded-xl p-5">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <FileText size={16} className="text-rr-orange" />
        <p className="font-heading font-bold text-rr-navy">{inv.period_label} · ${inv.total_amount.toFixed(2)}</p>
      </div>
      <div className="flex items-center gap-2">
        <select
          value={inv.status}
          onChange={(e) => onStatusChange(inv.id, e.target.value)}
          data-testid={`broker-invoice-status-select-${inv.id}`}
          className="border border-rr-line rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-rr-orange capitalize"
        >
          {["draft", "sent", "paid"].map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <button onClick={() => onToggleDetails(inv.id)} data-testid={`broker-invoice-details-toggle-${inv.id}`} className="text-xs font-bold text-rr-orange hover:underline">
          Details
        </button>
      </div>
    </div>
    {expanded && (
      <div data-testid={`broker-invoice-line-items-${inv.id}`} className="mt-4 pt-4 border-t border-rr-line space-y-1.5">
        {lineItems.map((li) => (
          <div key={li.id} className="flex items-center justify-between text-xs text-rr-muted">
            <span>{li.origin} → {li.destination}</span>
            <span>${logTotal(li).toFixed(2)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const BrokerBillingTab = () => {
  const [invoices, setInvoices] = useState(null);
  const [approvedLogs, setApprovedLogs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [lineItems, setLineItems] = useState([]);

  const loadInvoices = () => brokerApi.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
  const loadApprovedLogs = () =>
    brokerApi.get("/service-logs", { status: "approved" }).then(({ data }) => setApprovedLogs(data.filter((l) => !l.invoiced))).catch(() => setApprovedLogs([]));

  useEffect(() => { loadInvoices(); loadApprovedLogs(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await brokerApi.patch(`/invoices/${id}`, { status });
      toast.success("Invoice updated.");
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update invoice.");
    }
  };

  const toggleDetails = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    const { data } = await brokerApi.get(`/invoices/${id}`);
    setLineItems(data.line_items || []);
    setExpanded(id);
  };

  const onInvoiceCreated = () => { loadInvoices(); loadApprovedLogs(); };

  if (!invoices) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-rr-orange" /></div>;

  return (
    <div data-testid="broker-billing-tab" className="space-y-10">
      <div>
        <h2 className="font-heading text-xl font-bold text-rr-navy mb-4">Generate an Invoice</h2>
        <CreateInvoiceForm approvedLogs={approvedLogs} onCreated={onInvoiceCreated} />
      </div>

      <div>
        <h2 className="font-heading text-xl font-bold text-rr-navy mb-4">Invoices</h2>
        {invoices.length === 0 && <p data-testid="broker-invoices-empty-state" className="text-rr-muted text-sm">No invoices yet.</p>}
        <div className="space-y-3">
          {invoices.map((inv) => (
            <InvoiceRow
              key={inv.id}
              invoice={inv}
              expanded={expanded === inv.id}
              lineItems={lineItems}
              onToggleDetails={toggleDetails}
              onStatusChange={updateStatus}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
